import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import ProDrawer from "../components/ProDrawer";
import AppLogo from "../components/AppLogo";
import Footer from "../components/Footer";
import { useAuth } from '../context/AuthContext';
import { useLoginModal } from '../components/LoginModalProvider';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { supabase } from '../lib/supabase';

/* ── Fonts ── */
if (typeof document !== "undefined" && !document.getElementById("__srp_fonts")) {
  const l = document.createElement("link");
  l.id = "__srp_fonts";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
  document.head.appendChild(l);
}

/* ── Design tokens ── */
const C = {
  green:       "#1e9254",
  greenDark:   "#155c36",
  greenLight:  "#2ab76b",
  greenTint:   "#eef8f3",
  greenBorder: "#a7d9bc",
  greenMid:    "#c6e8d4",
  text:        "#0f1117",
  textMid:     "#2d3549",
  sub:         "#4a5268",
  faint:       "#8a93a8",
  ghost:       "#b0b8cc",
  border:      "#EDEFF2",
  borderFaint: "#F5F6F8",
  white:       "#FFFFFF",
  bg:          "#FAFAFB",
  serif:       "'Instrument Serif', Georgia, serif",
  sans:        "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  tx:          "all 0.18s cubic-bezier(.4,0,.2,1)",
};

/* ── Static options ── */
const ALL_SUGGESTIONS = [
  "Cleaning & Laundry","Carpet Cleaning","Deep House Cleaning","Office Cleaning",
  "Plumbing Services","Pipe Repair","Drain Unblocking","Water Heater Installation",
  "Electrical Installation","Solar Panel Setup","Generator Repair","AC Installation & Repair",
  "Hair Braiding","Hair Coloring","Nails & Manicure","Makeup Artist","Barber Services",
  "Boda Boda Rides","Boda Boda Delivery","Courier Delivery","Moving & Relocation",
  "Photography","Event Photography","Portrait Photography","Videography",
  "Home Tutoring","KCSE Exam Preparation","Babysitting","Nanny Services",
  "Personal Trainer","Yoga Instructor","Massage Therapy","Private Chef",
  "Carpentry","Furniture Assembly","Interior Painting","Gardening & Landscaping",
  "TV Repair","Appliance Repair","Phone Repair","AC Repair","Dog Grooming",
];

const BUDGET_OPTIONS = [
  { label: "Any Budget",       min: 0,    max: Infinity },
  { label: "Under Ksh 500",   min: 0,    max: 499      },
  { label: "Ksh 500 – 1,000", min: 500,  max: 1000     },
  { label: "Ksh 1,000 – 3,000",min:1000, max: 3000     },
  { label: "Over Ksh 3,000",  min: 3001, max: Infinity },
];
const SUCCESS_OPTIONS = ["Any", "80% & above", "90% & above", "95% & above"];
const SORT_OPTIONS    = ["Best Match", "Price: Low to High", "Price: High to Low", "Top Rated"];

const CATEGORY_SUBSERVICES = {
  cleaning:    ["Deep House Cleaning","Carpet & Upholstery","Office Cleaning","Laundry & Dry Cleaning","Airbnb Cleaning"],
  plumbing:    ["Pipe Repair","Drain Unblocking","Water Heater Installation","Tap Installation","Borehole Services"],
  electrical:  ["Electrical Installation","Solar Panel Setup","Generator Repair","Electrical Wiring","AC Installation & Repair"],
  "electronics-repair": ["TV Repair","Appliance Repair","Phone Repair","AC Repair"],
  "hair-beauty": ["Hair Braiding","Hair Coloring","Nails & Manicure","Makeup Artist","Barber Services"],
  "home-tutoring": ["Primary Tutoring","High School Subjects","KCSE Exam Preparation","Mathematics Tutoring"],
  photography: ["Event Photography","Portrait Photography","Videography","Product Shoots"],
  gardening:   ["Lawn Mowing","Garden Design","Tree Trimming","Dog Grooming"],
  "carpentry-furniture": ["Custom Furniture","Cabinet Installation","Furniture Assembly","Furniture Repair","Interior Painting"],
  moving:      ["House Moving","Office Relocation","Packing Services","Furniture Delivery"],
  childcare:   ["Babysitting","Nanny Services","After-School Care"],
  fitness:     ["Personal Trainer","Home Workouts","Yoga Instructor","Massage Therapy"],
  "auto-repair": ["Engine Diagnostics","Brake Repair","Oil Change","Car Servicing"],
  "errands-delivery": ["Grocery Shopping","Package Delivery","Queue Standing"],
  chef:        ["Private Chef","Catering Services","Meal Prep"],
};

const FALLBACK_CATEGORIES = [
  { id:"cleaning",    name:"Cleaning & Laundry",      slug:"cleaning"           },
  { id:"plumbing",    name:"Plumbing Services",        slug:"plumbing"           },
  { id:"electrical",  name:"Electrical Services",      slug:"electrical"         },
  { id:"electronics", name:"TV & Electronics Repair",  slug:"electronics-repair" },
  { id:"beauty",      name:"Hair & Beauty",            slug:"hair-beauty"        },
  { id:"tutoring",    name:"Home Tutoring",            slug:"home-tutoring"      },
  { id:"photography", name:"Photography",              slug:"photography"        },
  { id:"gardening",   name:"Gardening & Landscaping",  slug:"gardening"          },
  { id:"carpentry",   name:"Carpentry & Furniture",    slug:"carpentry-furniture"},
  { id:"moving",      name:"Moving & Relocation",      slug:"moving"             },
  { id:"childcare",   name:"Babysitting & Childcare",  slug:"childcare"          },
  { id:"fitness",     name:"Fitness & Training",       slug:"fitness"            },
  { id:"auto",        name:"Auto Repair",              slug:"auto-repair"        },
  { id:"errands",     name:"Errands & Delivery",       slug:"errands-delivery"   },
  { id:"chef",        name:"Personal Chef",            slug:"chef"               },
];

/* ── Style injection ── */
if (!document.getElementById("srp-style")) {
  const s = document.createElement("style");
  s.id = "srp-style";
  s.textContent = `
    @keyframes srp-shimmer {
      0%   { background-position: -600px 0; }
      100% { background-position:  600px 0; }
    }
    .srp-shimmer {
      background: linear-gradient(90deg,#f0f2f5 25%,#e4e7ec 50%,#f0f2f5 75%);
      background-size: 600px 100%;
      animation: srp-shimmer 1.4s infinite linear;
    }
    .srp-card:hover  { background: ${C.bg} !important; border-color: ${C.greenBorder} !important; }
    .srp-pill:hover  { background: ${C.greenTint} !important; border-color: ${C.greenBorder} !important; color: ${C.green} !important; }
    .srp-sugg:hover  { background: ${C.bg} !important; }
    .srp-cat:hover   { color: ${C.green} !important; background: ${C.greenTint} !important; }
    .srp-soc-link:hover { color: ${C.green} !important; }
    #srp-tab-scroll::-webkit-scrollbar { display: none; }
    #srp-loc-drop::-webkit-scrollbar  { width: 3px; }
    #srp-loc-drop::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 99px; }
    @media (max-width: 767px) { .srp-sidebar { display: none !important; } }
  `;
  document.head.appendChild(s);
}

/* ── Helpers ── */
const AVATAR_COLORS = ["#16a34a","#0284c7","#7c3aed","#dc2626","#d97706","#0891b2","#db2777","#9333ea"];
function avatarBg(name="") { return AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length]; }
function initials(name="") { const p=name.trim().split(" "); return (p[0]?.[0]||"")+(p[1]?.[0]||""); }

/* ── Shimmer skeleton ── */
function SkeletonCard() {
  return (
    <div>
      <div className="srp-shimmer" style={{width:"100%",aspectRatio:"3/4",borderRadius:14,marginBottom:8}}/>
      <div className="srp-shimmer" style={{width:"60%",height:13,borderRadius:4,marginBottom:6}}/>
      <div className="srp-shimmer" style={{width:"40%",height:11,borderRadius:4}}/>
    </div>
  );
}

/* ── Brand pulse loader (two circles, async pulse) ── */
if (!document.getElementById("srp-pulse-style")) {
  const s = document.createElement("style");
  s.id = "srp-pulse-style";
  s.textContent = `
    @media (prefers-reduced-motion: no-preference) {
      @keyframes srp-pulse-a { 0%,100% { transform: scale(1); } 50% { transform: scale(1.25); } }
      @keyframes srp-pulse-b { 0%,100% { transform: scale(1.25); } 50% { transform: scale(1); } }
      .srp-pulse-a { animation: srp-pulse-a 1.1s ease-in-out infinite; }
      .srp-pulse-b { animation: srp-pulse-b 1.1s ease-in-out infinite; }
    }
  `;
  document.head.appendChild(s);
}
function SearchLoadingIndicator({ isMobile }) {
  return (
    <div style={{textAlign:"center",padding:isMobile?"52px 16px":"80px 24px"}}>
      <div style={{position:"relative",width:52,height:40,margin:"0 auto 20px"}}>
        <div className="srp-pulse-a" style={{position:"absolute",left:0,top:6,width:26,height:26,borderRadius:"50%",background:"#5DCAA5",transformOrigin:"center"}}/>
        <div className="srp-pulse-b" style={{position:"absolute",left:16,top:6,width:26,height:26,borderRadius:"50%",background:C.greenDark,transformOrigin:"center"}}/>
      </div>
      <p style={{fontFamily:C.serif,fontSize:isMobile?18:20,fontStyle:"italic",color:C.text,margin:0,letterSpacing:"-0.02em"}}>
        Finding JiraniPros for you…
      </p>
    </div>
  );
}

/* ── Related search pill ── */
function RelatedPill({ label, onClick, isMobile }) {
  return (
    <button className="srp-pill" onClick={onClick} style={{
      display:"inline-flex",alignItems:"center",gap:7,
      padding:isMobile?"8px 14px":"9px 16px",
      border:`1.5px solid ${C.border}`,borderRadius:999,
      background:C.bg,cursor:"pointer",
      fontSize:isMobile?12:12.5,color:C.sub,
      fontFamily:C.sans,transition:C.tx,
    }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={C.ghost} strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
      {label}
    </button>
  );
}

/* ── Star rating ── */
function Stars({ rating=0, size=12 }) {
  return (
    <span style={{display:"inline-flex",gap:1,alignItems:"center"}}>
      {[1,2,3,4,5].map(i=>(
        <svg key={i} width={size} height={size} viewBox="0 0 20 20"
          fill={i<=Math.round(rating)?"#FBBF24":"#e5e7eb"}>
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
        </svg>
      ))}
    </span>
  );
}

/* ── Tooltip ── */
function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  const { isMobile } = useBreakpoint();
  return (
    <div style={{position:"relative",display:"inline-flex"}}
      onMouseEnter={()=>setShow(true)} onMouseLeave={()=>setShow(false)}>
      {children}
      {show && !isMobile && (
        <div style={{
          position:"absolute",bottom:"calc(100% + 8px)",left:"50%",
          transform:"translateX(-50%)",background:C.text,color:"#fff",
          fontSize:11,padding:"6px 10px",borderRadius:8,
          whiteSpace:"nowrap",zIndex:500,
          boxShadow:"0 4px 12px rgba(0,0,0,.18)",pointerEvents:"none",
          fontFamily:C.sans,lineHeight:1.5,
        }}>
          {text}
          <div style={{position:"absolute",top:"100%",left:"50%",transform:"translateX(-50%)",
            width:0,height:0,borderLeft:"5px solid transparent",
            borderRight:"5px solid transparent",borderTop:`5px solid ${C.text}`}}/>
        </div>
      )}
    </div>
  );
}

/* ── Pro Card ── */
function ProCard({ pro, onClick, ctaLabel="View profile", ctaHref=null }) {
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const [imgErr, setImgErr] = useState(false);

  const name        = pro.full_name || pro.name || "JiraniPro";
  const serviceName = pro.primaryService || pro.service || "Professional Services";
  const city        = pro.location || pro.city || "";
  const price       = pro.baseRate || pro.price || null;
  const rating      = pro.rating || 0;
  const reviews     = pro.reviews || 0;
  const isVerified  = pro.is_approved !== false;
  const isAvailable = pro.available !== false;
  const bg          = avatarBg(name);

  const handleCta = (e) => {
    e.stopPropagation();
    if (ctaHref) navigate(ctaHref);
    else onClick();
  };

  return (
    <div onClick={onClick} style={{ cursor:"pointer" }}>
      <div style={{
        position:"relative", width:"100%",
        aspectRatio: isMobile ? "1 / 1" : "3 / 4",
        borderRadius:14, overflow:"hidden", background:bg,
      }}>
        {pro.profile_photo && !imgErr ? (
          <img src={pro.profile_photo} alt={name}
            onError={()=>setImgErr(true)}
            style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
        ) : (
          <div style={{
            width:"100%",height:"100%",display:"flex",
            alignItems:"center",justifyContent:"center",
            fontFamily:C.sans,fontSize:isMobile?28:36,fontWeight:700,color:"#fff",
          }}>{initials(name)}</div>
        )}

        <div style={{
          position:"absolute",left:0,right:0,bottom:0,height:"58%",
          background:"linear-gradient(to top, rgba(0,0,0,0.78), rgba(0,0,0,0) 100%)",
          pointerEvents:"none",
        }}/>

        {isVerified && (
          <span style={{
            position:"absolute",top:8,left:8,
            background:"#fff",color:C.greenDark,
            fontSize:isMobile?9.5:10.5,fontWeight:600,
            padding:"3px 9px",borderRadius:999,
            display:"flex",alignItems:"center",gap:3,fontFamily:C.sans,
          }}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
              <path d="M9 12l2 2 4-4"/>
            </svg>
            ID-verified
          </span>
        )}

        {isAvailable && (
          <span style={{
            position:"absolute",top:8,right:8,
            width:9,height:9,borderRadius:"50%",
            background:C.green,border:"2px solid #fff",
          }}/>
        )}

        <button onClick={handleCta} style={{
          position:"absolute",right:9,bottom: isMobile?38:44,
          background:C.green,color:"#fff",border:"none",
          borderRadius:999,padding: isMobile?"5px 10px":"6px 13px",
          fontSize:isMobile?10.5:11.5,fontWeight:600,cursor:"pointer",
          fontFamily:C.sans,
        }}>{ctaLabel}</button>

        <div style={{position:"absolute",left:11,right:11,bottom:9}}>
          <p style={{margin:0,fontSize:isMobile?12.5:14,fontWeight:600,color:"#fff",fontFamily:C.sans,
            whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
            {name}
          </p>
          <p style={{margin:"2px 0 2px",fontSize:isMobile?10:11,color:"#e5e5e0",fontFamily:C.sans,
            whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
            {serviceName}{city?` · ${city}`:""}
          </p>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            {reviews>0 && (
              <span style={{fontSize:isMobile?10:11,color:"#8FE0B0",fontWeight:600,fontFamily:C.sans,display:"flex",alignItems:"center",gap:3}}>
                <svg width="9" height="9" viewBox="0 0 20 20" fill="#FBBF24"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                {rating.toFixed(1)}
              </span>
            )}
            {price && (
              <span style={{fontSize:isMobile?10:11,color:"#e5e5e0",fontFamily:C.sans}}>
                Ksh {price.toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Sidebar filter section ── */
function FilterSection({ title, children, defaultOpen=true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{borderBottom:`1px solid ${C.borderFaint}`,paddingBottom:16,marginBottom:16}}>
      <button onClick={()=>setOpen(o=>!o)} style={{
        width:"100%", display:"flex", alignItems:"center",
        justifyContent:"space-between", background:"none", border:"none",
        cursor:"pointer", padding:"0 0 10px", fontFamily:C.sans,
      }}>
        <span style={{fontSize:11,fontWeight:700,color:C.ghost,textTransform:"uppercase",letterSpacing:".08em"}}>
          {title}
        </span>
        <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke={C.ghost} strokeWidth="2.5"
          style={{transition:"transform .18s",transform:open?"rotate(180deg)":"rotate(0)"}}>
          <path d="M5 8l5 5 5-5"/>
        </svg>
      </button>
      {open && children}
    </div>
  );
}

/* ── Radio option ── */
function RadioOpt({ name, label, checked, onChange }) {
  return (
    <label style={{display:"flex",alignItems:"center",gap:9,padding:"4px 0",cursor:"pointer"}}>
      <input type="radio" name={name} checked={checked} onChange={onChange}
        style={{accentColor:C.green,cursor:"pointer"}}/>
      <span style={{fontSize:13,color:checked?C.green:C.sub,fontFamily:C.sans,fontWeight:checked?600:400,transition:C.tx}}>
        {label}
      </span>
    </label>
  );
}

/* ── Empty state ── */
function EmptyState({ onClear, isMobile }) {
  return (
    <div style={{textAlign:"center",padding:isMobile?"52px 16px":"80px 24px"}}>
      <div style={{
        width:64,height:64,borderRadius:"50%",
        background:C.greenTint,border:`2px solid ${C.greenBorder}`,
        display:"flex",alignItems:"center",justifyContent:"center",
        margin:"0 auto 20px",
      }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/>
          <path d="M21 21l-4.35-4.35"/>
          <path d="M8 11h6M11 8v6" opacity=".4"/>
        </svg>
      </div>
      <p style={{fontFamily:C.serif,fontSize:isMobile?20:24,fontStyle:"italic",color:C.text,margin:"0 0 8px",letterSpacing:"-0.02em"}}>
        No JiraniPros found
      </p>
      <p style={{fontFamily:C.sans,fontSize:isMobile?13:14,color:C.faint,margin:"0 0 24px",lineHeight:1.65,maxWidth:300,marginLeft:"auto",marginRight:"auto"}}>
        Try a different search or browse all available professionals across Kenya.
      </p>
      <button onClick={onClear} style={{
        background:C.green, color:"#fff", border:"none",
        borderRadius:999, padding:"11px 26px",
        fontSize:13.5, fontWeight:600, cursor:"pointer", fontFamily:C.sans,
        boxShadow:`0 4px 16px rgba(30,146,84,.25)`,
      }}>Browse all JiraniPros</button>
    </div>
  );
}

/* ── Mobile filter sheet ── */
function MobileFilterSheet({ open, onClose, locationOptions, location, setLocation, budget, setBudget, successMin, setSuccessMin, sortBy, setSortBy, availableOnly, setAvailableOnly, onApply, categories, catsLoading, activeCategory, onCategorySelect }) {
  const [overlay, setOverlay] = useState(open);
  useEffect(()=>{ if(open) setOverlay(true); },[open]);
  const handleClose=()=>{ onClose(); setTimeout(()=>setOverlay(false),300); };
  if(!overlay) return null;
  return (
    <div style={{position:"fixed",inset:0,zIndex:900,display:"flex",flexDirection:"column",justifyContent:"flex-end"}}>
      <div onClick={handleClose} style={{position:"absolute",inset:0,background:"rgba(0,0,0,.45)",opacity:open?1:0,transition:"opacity .25s"}}/>
      <div style={{
        position:"relative",background:C.white,borderRadius:"20px 20px 0 0",
        maxHeight:"85vh",
        display:"flex",flexDirection:"column",
        transform:open?"translateY(0)":"translateY(100%)",
        transition:"transform .28s cubic-bezier(.32,.72,0,1)",
      }}>
        <div style={{display:"flex",justifyContent:"center",padding:"12px 0 0",flexShrink:0}}>
          <div style={{width:36,height:4,borderRadius:2,background:C.border}}/>
        </div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px 12px",borderBottom:`1px solid ${C.borderFaint}`,flexShrink:0}}>
          <span style={{fontSize:16,fontWeight:700,color:C.text,fontFamily:C.sans}}>Filters</span>
          <button onClick={handleClose} style={{background:"none",border:"none",cursor:"pointer",padding:4,color:C.faint}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <div style={{padding:"20px",overflowY:"auto",flex:1,WebkitOverflowScrolling:"touch"}}>
          <div style={{marginBottom:24}}>
            <p style={{fontSize:11,fontWeight:700,color:C.ghost,textTransform:"uppercase",letterSpacing:".08em",margin:"0 0 10px",fontFamily:C.sans}}>Category</p>
            <div onClick={()=>onCategorySelect(null)} style={{
              padding:"8px 0",cursor:"pointer",fontSize:13.5,fontFamily:C.sans,
              color:!activeCategory?C.green:C.sub,fontWeight:!activeCategory?600:400,
            }}>All categories</div>
            {catsLoading ? (
              [1,2,3].map(i=><div key={i} className="srp-shimmer" style={{width:"60%",height:12,borderRadius:4,margin:"8px 0"}}/>)
            ) : (
              (categories.length>0?categories:FALLBACK_CATEGORIES).map(cat=>{
                const isActive = activeCategory?.id===cat.id;
                const subs = CATEGORY_SUBSERVICES[cat.slug] || [];
                return (
                  <div key={cat.id}>
                    <div onClick={()=>onCategorySelect(cat)} style={{
                      padding:"8px 0",cursor:"pointer",fontSize:13.5,fontFamily:C.sans,
                      color:isActive?C.green:C.sub,fontWeight:isActive?600:400,
                      display:"flex",alignItems:"center",justifyContent:"space-between",
                    }}>
                      <span>{cat.name}</span>
                      {subs.length>0 && isActive && (
                        <svg width="10" height="10" viewBox="0 0 20 20" fill="none" stroke={C.green} strokeWidth="2.5" style={{transform:"rotate(180deg)"}}>
                          <path d="M5 8l5 5 5-5"/>
                        </svg>
                      )}
                    </div>
                    {isActive && subs.length>0 && (
                      <div style={{paddingLeft:14,marginTop:2,marginBottom:4}}>
                        {subs.map(sub=>(
                          <div key={sub} onClick={()=>onCategorySelect(cat)} style={{
                            padding:"5px 0",cursor:"pointer",fontSize:12,fontFamily:C.sans,
                            color:C.faint,
                          }}>{sub}</div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div style={{marginBottom:24}}>
            <p style={{fontSize:11,fontWeight:700,color:C.ghost,textTransform:"uppercase",letterSpacing:".08em",margin:"0 0 10px",fontFamily:C.sans}}>Sort by</p>
            <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
              {SORT_OPTIONS.map(s=>(
                <button key={s} onClick={()=>setSortBy(s)} style={{
                  padding:"8px 14px",borderRadius:999,
                  border:`1.5px solid ${sortBy===s?C.green:C.border}`,
                  background:sortBy===s?C.greenTint:C.white,
                  color:sortBy===s?C.green:C.sub,
                  fontSize:12,fontWeight:sortBy===s?600:400,
                  cursor:"pointer",fontFamily:C.sans,transition:C.tx,
                }}>{s}</button>
              ))}
            </div>
          </div>
          <div style={{marginBottom:24}}>
            <p style={{fontSize:11,fontWeight:700,color:C.ghost,textTransform:"uppercase",letterSpacing:".08em",margin:"0 0 10px",fontFamily:C.sans}}>Budget per job</p>
            {BUDGET_OPTIONS.map(b=>(
              <RadioOpt key={b.label} name="mbudget" label={b.label} checked={budget===b.label} onChange={()=>setBudget(b.label)}/>
            ))}
          </div>
          <div style={{marginBottom:24}}>
            <p style={{fontSize:11,fontWeight:700,color:C.ghost,textTransform:"uppercase",letterSpacing:".08em",margin:"0 0 10px",fontFamily:C.sans}}>Location</p>
            <select value={location} onChange={e=>setLocation(e.target.value)}
              style={{width:"100%",padding:"10px 12px",borderRadius:10,border:`1.5px solid ${C.border}`,fontSize:14,fontFamily:C.sans,color:C.sub,background:C.white,outline:"none",minHeight:44}}>
              {locationOptions.map(l=><option key={l}>{l}</option>)}
            </select>
          </div>
          <div style={{marginBottom:24}}>
            <p style={{fontSize:11,fontWeight:700,color:C.ghost,textTransform:"uppercase",letterSpacing:".08em",margin:"0 0 10px",fontFamily:C.sans}}>Job success</p>
            {SUCCESS_OPTIONS.map(s=>(
              <RadioOpt key={s} name="msuccess" label={s} checked={successMin===s} onChange={()=>setSuccessMin(s)}/>
            ))}
          </div>
          <div style={{marginBottom:8}}>
            <label style={{display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer"}}>
              <div>
                <p style={{fontSize:13.5,fontWeight:600,color:C.text,margin:"0 0 2px",fontFamily:C.sans}}>Available now</p>
                <p style={{fontSize:12,color:C.faint,margin:0,fontFamily:C.sans}}>Only show online pros</p>
              </div>
              <div onClick={()=>setAvailableOnly(!availableOnly)} style={{
                width:44,height:24,borderRadius:12,cursor:"pointer",
                background:availableOnly?C.green:C.border,
                position:"relative",transition:"background .2s",flexShrink:0,
              }}>
                <div style={{
                  position:"absolute",top:3,left:availableOnly?23:3,
                  width:18,height:18,borderRadius:"50%",background:C.white,
                  boxShadow:"0 1px 3px rgba(0,0,0,.2)",transition:"left .2s",
                }}/>
              </div>
            </label>
          </div>
        </div>

        <div style={{padding:"14px 20px",display:"flex",gap:10,borderTop:`1px solid ${C.borderFaint}`,background:C.white,flexShrink:0}}>
          <button onClick={()=>{setLocation("All Locations");setBudget("Any Budget");setSuccessMin("Any");setAvailableOnly(false);setSortBy("Best Match");onCategorySelect(null);}}
            style={{flex:1,padding:"13px",borderRadius:10,border:`1.5px solid ${C.border}`,background:C.white,color:C.textMid,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:C.sans}}>
            Clear all
          </button>
          <button onClick={()=>{onApply();handleClose();}} style={{
            flex:2,padding:"13px",borderRadius:10,border:"none",
            background:C.green,color:"#fff",fontSize:14,fontWeight:700,
            cursor:"pointer",fontFamily:C.sans,
          }}>Show results</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════ MAIN PAGE ═══════════════════════ */
export default function SearchResultsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useBreakpoint();
  const { isLoggedIn } = useAuth();
  const { open: openLogin } = useLoginModal();

  const urlQuery    = searchParams.get("q") || "";
  const urlCategory = searchParams.get("category") || "";
  const urlLocation = searchParams.get("location") || "";
  const urlMode     = searchParams.get("mode") || "";
  const urlContext  = searchParams.get("context") || "";
  const isBusinessMode = urlMode === "business-pro";
  const ctaLabel = urlContext === "reserve" ? "Reserve this Pro" : "View profile";

  /* ── State ── */
  const [inputVal, setInputVal]               = useState(urlQuery);
  const [query, setQuery]                     = useState(urlQuery);
  const [suggestions, setSuggestions]         = useState([]);
  const [showSugg, setShowSugg]               = useState(false);
  const [hasTyped, setHasTyped]               = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const [pros, setPros]                       = useState([]);
  const [prosLoading, setProsLoading]         = useState(true);

  const [categories, setCategories]           = useState([]);
  const [catsLoading, setCatsLoading]         = useState(true);
  const [activeCategory, setActiveCategory]   = useState(null);

  const [location, setLocation]               = useState(urlLocation || "All Locations");
  const [budget, setBudget]                   = useState("Any Budget");
  const [successMin, setSuccessMin]           = useState("Any");
  const [availableOnly, setAvailableOnly]     = useState(false);
  const [sortBy, setSortBy]                   = useState("Best Match");

  const [currentPage, setCurrentPage]         = useState(1);
  const [selectedPro, setSelectedPro]         = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showLocationDropSearch, setShowLocationDropSearch]   = useState(false);
  const [showLocationDropSidebar, setShowLocationDropSidebar] = useState(false);

  const searchLocationDropRef  = useRef(null);
  const sidebarLocationDropRef = useRef(null);
  const searchWrapRef          = useRef(null);
  const tabScrollRef    = useRef(null);

  const PER_PAGE = isMobile ? 6 : 9;

  const KENYAN_CITIES = [
    "Nairobi","Mombasa","Kisumu","Nakuru","Eldoret","Thika","Nyeri",
    "Machakos","Meru","Kitale","Malindi","Garissa","Kakamega","Kisii",
    "Lamu","Naivasha","Nanyuki","Embu","Isiolo","Voi",
  ];

  const SUBREGIONS = {
    Nairobi: ["Westlands","Kilimani","Kileleshwa","Lavington","Karen","Langata","Embakasi","Kasarani","Ruaka","Runda","Muthaiga","Parklands","South B","South C","Upper Hill","CBD"],
    Mombasa: ["Nyali","Bamburi","Kizingo","Tudor","Likoni","Changamwe","Kisauni","Shanzu","Old Town"],
  };

  /* ── Location options ── */
  const locationOptions = useMemo(()=>{
    const seen = new Set(KENYAN_CITIES.map(c=>c.toLowerCase()));
    const extra = [];
    pros.forEach(p=>{
      const raw=(p.location||"").trim();
      if(!raw) return;
      const n = raw.charAt(0).toUpperCase()+raw.slice(1);
      if(!seen.has(n.toLowerCase())){ seen.add(n.toLowerCase()); extra.push(n); }
    });
    return ["All Locations",...[...KENYAN_CITIES,...extra].sort((a,b)=>a.localeCompare(b))];
  },[pros]);

  /* ── Load categories ── */
  useEffect(()=>{
    (async()=>{
      setCatsLoading(true);
      try{ const{data}=await supabase.from('categories').select('*').order('sort_order',{ascending:true,nullsFirst:false}); setCategories(data||[]); }catch(_){}
      finally{ setCatsLoading(false); }
    })();
  },[]);

  /* ── Sync active category from URL ── */
  useEffect(()=>{
    if(urlCategory && categories.length>0){
      const m=categories.find(c=>c.slug===urlCategory||c.slug===urlCategory.toLowerCase()||c.name.toLowerCase().replace(/\s+/g,"-")===urlCategory.toLowerCase()||c.name.toLowerCase()===urlCategory.toLowerCase());
      setActiveCategory(m||null);
    } else { setActiveCategory(null); }
  },[urlCategory,categories]);

  /* ── Sync query/location from URL ── */
  useEffect(()=>{ setQuery(urlQuery); setInputVal(urlQuery); setHasTyped(false); setCurrentPage(1); },[urlQuery]);
  useEffect(()=>{ setLocation(urlLocation||"All Locations"); },[urlLocation]);
  useEffect(()=>{ setCurrentPage(1); },[activeCategory,location,budget,successMin,availableOnly]);

  /* ── Close dropdowns on outside click ── */
  useEffect(()=>{
    const h=e=>{
      if(searchLocationDropRef.current && !searchLocationDropRef.current.contains(e.target)) setShowLocationDropSearch(false);
      if(sidebarLocationDropRef.current && !sidebarLocationDropRef.current.contains(e.target)) setShowLocationDropSidebar(false);
    };
    document.addEventListener("mousedown",h); return()=>document.removeEventListener("mousedown",h);
  },[]);
  useEffect(()=>{
    const h=e=>{ if(searchWrapRef.current&&!searchWrapRef.current.contains(e.target)) setShowSugg(false); };
    document.addEventListener("mousedown",h); return()=>document.removeEventListener("mousedown",h);
  },[]);

  /* ── Load pros ── */
  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      setProsLoading(true);
      try{
        let q=supabase.from('pro_profiles').select(`id,full_name,phone,location,bio,updated_at,availability,business_pro_approved,profiles(avatar_url),pro_skills(id,base_rate,experience_level,is_approved,is_active,category_id,categories(id,name,slug))`);
        if(isBusinessMode) q=q.eq("business_pro_approved",true);
        const{data,error}=await q;
        if(error||!data||cancelled) return;
        const proIds=data.map(p=>p.id);
        const{data:reviewRows}=await supabase.from('reviews').select('pro_id,rating').in('pro_id',proIds);
        const reviewStats={};
        (reviewRows||[]).forEach(r=>{ if(!reviewStats[r.pro_id]) reviewStats[r.pro_id]={sum:0,count:0}; reviewStats[r.pro_id].sum+=r.rating; reviewStats[r.pro_id].count+=1; });
                const mapped=data.map(pro=>{
          const activeSkills=(pro.pro_skills||[]).filter(s=>s.is_approved&&s.is_active);
          const catMatch=activeCategory?activeSkills.find(s=>s.categories?.id===activeCategory.id):null;
          const primarySkill=catMatch||activeSkills[0];
          return{
            ...pro,
            profile_photo:pro.profiles?.avatar_url||null,
            _rawSkills:activeSkills,
            primaryService:primarySkill?.categories?.name||null,
            primaryCategoryId:primarySkill?.categories?.id||null,
            primaryCategorySlug:primarySkill?.categories?.slug||null,
            baseRate:primarySkill?.base_rate||null,
            allCategoryIds:activeSkills.map(s=>s.categories?.id).filter(Boolean),
            allCategorySlugs:activeSkills.map(s=>s.categories?.slug).filter(Boolean),
            rating:reviewStats[pro.id]?reviewStats[pro.id].sum/reviewStats[pro.id].count:0,
            reviews:reviewStats[pro.id]?.count||0,
            available:pro.availability==='available',
            is_approved:activeSkills.length>0,
          };
        }).filter(p=>p.is_approved&&p.primaryService);
        if(!cancelled) setPros(mapped);
      }catch(_){}
      finally{ if(!cancelled) setProsLoading(false); }
    })();
    return()=>{ cancelled=true; };
  },[isBusinessMode]);

  /* ── Autocomplete ── */
  useEffect(()=>{
    if(!hasTyped||inputVal.trim().length<2){ setSuggestions([]); setShowSugg(false); return; }
    const m=ALL_SUGGESTIONS.filter(s=>s.toLowerCase().includes(inputVal.toLowerCase())).slice(0,8);
    setSuggestions(m); setShowSugg(m.length>0);
  },[inputVal,hasTyped]);

  useEffect(()=>{ window.scrollTo(0,0); },[]);

  /* ── Broadcast search-active state so the navbar logo can animate ── */
  useEffect(()=>{
    const active = isSearchFocused || prosLoading;
    window.dispatchEvent(new CustomEvent("jiraniapp:search-active", { detail: active }));
  },[isSearchFocused, prosLoading]);

  /* ── Filter + sort ── */
  const filtered = useMemo(()=>{
    let list=pros;
    if(activeCategory){
      list=list.filter(p=>p.allCategoryIds?.includes(activeCategory.id)).map(p=>{
        const sk=(p._rawSkills||[]).find(s=>s.categories?.id===activeCategory.id);
        if(!sk) return p;
        return{...p,primaryService:sk.services?.name||p.primaryService,baseRate:sk.base_rate||p.baseRate};
      });
    }
    if(query.trim()){
      const q=query.toLowerCase();
      list=list.filter(p=>(p.full_name||"").toLowerCase().includes(q)||(p.primaryService||"").toLowerCase().includes(q));
    }
    if(location!=="All Locations"){ const l=location.toLowerCase(); list=list.filter(p=>(p.location||"").toLowerCase().includes(l)); }
    const bOpt=BUDGET_OPTIONS.find(b=>b.label===budget);
    if(bOpt&&bOpt.min>0) list=list.filter(p=>p.baseRate!=null&&p.baseRate>=bOpt.min&&p.baseRate<=bOpt.max);
    if(successMin!=="Any"){ const mr=successMin==="80% & above"?4.0:successMin==="90% & above"?4.5:4.75; list=list.filter(p=>p.rating>=mr); }
    if(availableOnly) list=list.filter(p=>p.available);
    if(sortBy==="Price: Low to High") list=[...list].sort((a,b)=>(a.baseRate||0)-(b.baseRate||0));
    else if(sortBy==="Price: High to Low") list=[...list].sort((a,b)=>(b.baseRate||0)-(a.baseRate||0));
    else if(sortBy==="Top Rated") list=[...list].sort((a,b)=>b.rating-a.rating);
    return list;
  },[pros,activeCategory,query,location,budget,successMin,availableOnly,sortBy]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const pagePros   = filtered.slice((currentPage-1)*PER_PAGE, currentPage*PER_PAGE);
  const hasActiveFilters = location!=="All Locations"||budget!=="Any Budget"||successMin!=="Any"||availableOnly;

  /* ── Handlers ── */
  const buildUrl = useCallback((overrides={})=>{
    const params=new URLSearchParams();
    const q=overrides.q!==undefined?overrides.q:query;
    const cat=overrides.cat!==undefined?overrides.cat:(activeCategory?.slug||"");
    const loc=overrides.loc!==undefined?overrides.loc:location;
    if(q) params.set("q",q);
    if(cat) params.set("category",cat);
    if(loc&&loc!=="All Locations") params.set("location",loc);
    if(urlMode) params.set("mode",urlMode);
    if(urlContext) params.set("context",urlContext);
    return"/search"+(params.toString()?"?"+params.toString():"");
  },[query,activeCategory,location,urlMode,urlContext]);

  const handleLocationChange = useCallback((nl)=>{ setLocation(nl); setCurrentPage(1); setShowLocationDropSearch(false); setShowLocationDropSidebar(false); navigate(buildUrl({loc:nl}),{replace:true}); },[navigate,buildUrl]);
  const handleSearch = useCallback((q)=>{ if(!q.trim()) return; setShowSugg(false); const p=new URLSearchParams(); p.set("q",q.trim()); if(urlMode) p.set("mode",urlMode); if(urlContext) p.set("context",urlContext); navigate(`/search?${p.toString()}`); },[navigate,urlMode,urlContext]);
  const handleCategoryTab = useCallback((cat)=>{ navigate(buildUrl({cat:cat?.slug||""})); },[navigate,buildUrl]);
  const handleProClick = useCallback((pro)=>{ if(!isLoggedIn) openLogin({ redirect: window.location.pathname + window.location.search }); else setSelectedPro(pro); },[isLoggedIn,navigate,openLogin]);
  const getCtaHref = useCallback((pro)=>{ if(urlContext==="reserve") return`/pro/${pro.id}?context=reserve`; return null; },[urlContext]);
  const clearAll = useCallback(()=>{ setLocation("All Locations"); setBudget("Any Budget"); setSuccessMin("Any"); setAvailableOnly(false); setSortBy("Best Match"); const p=new URLSearchParams(); if(urlMode) p.set("mode",urlMode); if(urlContext) p.set("context",urlContext); navigate("/search"+(p.toString()?"?"+p.toString():"")); },[navigate,urlMode,urlContext]);

  /* ── Tab scroll ── */
  const [tabCanScrollLeft,  setTabCanScrollLeft]  = useState(false);
  const [tabCanScrollRight, setTabCanScrollRight] = useState(false);
  const updateTabScroll = useCallback(()=>{ const el=tabScrollRef.current; if(!el) return; setTabCanScrollLeft(el.scrollLeft>8); setTabCanScrollRight(el.scrollLeft<el.scrollWidth-el.clientWidth-8); },[]);
  useEffect(()=>{
    const el=tabScrollRef.current; if(!el) return;
    updateTabScroll();
    const ro=new ResizeObserver(()=>updateTabScroll()); ro.observe(el);
    if(document.fonts?.ready) document.fonts.ready.then(updateTabScroll);
    window.addEventListener("resize",updateTabScroll);
    return()=>{ ro.disconnect(); window.removeEventListener("resize",updateTabScroll); };
  },[updateTabScroll,catsLoading,categories]);
  const scrollTabBar=(dir)=>{ tabScrollRef.current?.scrollBy({left:dir*240,behavior:"smooth"}); setTimeout(updateTabScroll,320); };

  const headingText = activeCategory ? activeCategory.name : query ? `Results for "${query}"` : "Browse All JiraniPros";

  const NAVBAR_H = isMobile ? 60 : 72;

  return (
    <div style={{fontFamily:C.sans,background:"#FFFFFF",minHeight:"100vh",paddingTop:NAVBAR_H}}>
      <ProDrawer pro={selectedPro} onClose={()=>setSelectedPro(null)}/>
      <MobileFilterSheet
        open={showMobileFilters} onClose={()=>setShowMobileFilters(false)}
        locationOptions={locationOptions}
        location={location} setLocation={handleLocationChange}
        budget={budget} setBudget={setBudget}
        successMin={successMin} setSuccessMin={setSuccessMin}
        sortBy={sortBy} setSortBy={setSortBy}
        availableOnly={availableOnly} setAvailableOnly={setAvailableOnly}
        onApply={()=>setCurrentPage(1)}
        categories={categories} catsLoading={catsLoading}
        activeCategory={activeCategory} onCategorySelect={handleCategoryTab}
      />

      {/* ── Search bar strip ── */}
      <div style={{
        padding:isMobile?"10px 0":"12px 0",
        background:"#FFFFFF",
        position:"sticky",top:NAVBAR_H,zIndex:100,
      }}>
        <div style={{maxWidth:1440,margin:"0 auto",padding:isMobile?"0 16px":"0 16px",display:"flex",alignItems:"center",gap:10}}>
          {/* Search input + location */}
          <div ref={searchWrapRef} style={{position:"relative",flex:1,maxWidth:isMobile?"100%":540}}>
            <div style={{
              display:"flex",alignItems:"center",
              border:`1.5px solid ${C.border}`,borderRadius:999,
              padding:isMobile?"8px 14px":"10px 16px",gap:8,background:C.white,
              boxShadow:"0 1px 4px rgba(0,0,0,.04)",
              transition:"border-color .15s,box-shadow .15s",
            }}
              onFocusCapture={e=>{e.currentTarget.style.borderColor=C.green;e.currentTarget.style.boxShadow=`0 0 0 3px ${C.greenTint}`;setIsSearchFocused(true);}}
              onBlurCapture={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.boxShadow="0 1px 4px rgba(0,0,0,.04)";setIsSearchFocused(false);}}>
              <button
                onClick={() => handleSearch(inputVal)}
                aria-label="Search"
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", color: C.ghost, flexShrink: 0 }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/>
                </svg>
              </button>
              <input
                value={inputVal}
                onChange={e=>{setInputVal(e.target.value);setHasTyped(true);}}
                onKeyDown={e=>{if(e.key==="Enter") handleSearch(inputVal);}}
                placeholder={isMobile ? "Search…" : "Search for a service or professional…"}
                style={{flex:1,minWidth:0,border:"none",outline:"none",fontSize:13.5,fontFamily:C.sans,color:C.text,background:"transparent"}}
              />
              {inputVal && (
                <button onClick={()=>{setInputVal("");setSuggestions([]);setShowSugg(false);handleSearch("");}}
                  style={{background:C.bg,border:"none",cursor:"pointer",padding:0,display:"flex",color:C.faint,borderRadius:"50%",width:20,height:20,alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              )}
              <div style={{width:1,height:16,background:C.border,flexShrink:0}}/>
              <div ref={searchLocationDropRef} style={{position:"relative"}}>
                <button
                  onClick={()=>setShowLocationDropSearch(o=>!o)}
                  style={{
                    display:"flex",alignItems:"center",gap:4,background:"none",border:"none",
                    cursor:"pointer",fontFamily:C.sans,fontSize:12.5,color:C.sub,
                    whiteSpace:"nowrap",flexShrink:0,padding:0,
                  }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.ghost} strokeWidth="2.2" style={{flexShrink:0}}>
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/>
                  </svg>
                  {isMobile ? "" : location==="All Locations" ? "Anywhere" : location}
                </button>
                {showLocationDropSearch && (
                  <div id="srp-loc-drop" style={{
                    position:"absolute",top:"calc(100% + 10px)",right:0,
                    width:220,background:C.white,border:`1.5px solid ${C.border}`,borderRadius:12,
                    boxShadow:"0 8px 28px rgba(0,0,0,.10)",zIndex:9999,
                    maxHeight:280,overflowY:"auto",
                  }}>
                    {locationOptions.map(l=>{
                      const subs = SUBREGIONS[l];
                      const isExpanded = subs && (location===l || subs.includes(location));
                      return (
                        <div key={l}>
                          <div onClick={()=>{ if(!subs) { handleLocationChange(l); setShowLocationDropSearch(false); } else { handleLocationChange(l); } }}
                            style={{
                              padding:"9px 12px",fontSize:13,fontFamily:C.sans,
                              color:(l===location||subs?.includes(location))?C.green:C.sub,
                              fontWeight:(l===location||subs?.includes(location))?600:400,
                              background:(l===location)?C.greenTint:C.white,
                              cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",
                            }}>
                            <span>{l}</span>
                            {subs && (
                              <svg width="10" height="10" viewBox="0 0 20 20" fill="none" stroke={C.ghost} strokeWidth="2.5"
                                style={{transform:isExpanded?"rotate(180deg)":"rotate(0)",transition:"transform .15s"}}>
                                <path d="M5 8l5 5 5-5"/>
                              </svg>
                            )}
                          </div>
                          {subs && isExpanded && subs.map(sub=>(
                            <div key={sub} onClick={()=>{handleLocationChange(sub);setShowLocationDropSearch(false);}}
                              style={{
                                padding:"7px 12px 7px 26px",fontSize:12,fontFamily:C.sans,
                                color:sub===location?C.green:C.faint,
                                fontWeight:sub===location?600:400,
                                background:sub===location?C.greenTint:C.white,
                                cursor:"pointer",
                              }}>{sub}</div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            {showSugg && (
              <div style={{position:"absolute",top:"calc(100% + 6px)",left:0,right:0,background:C.white,border:`1.5px solid ${C.border}`,borderRadius:14,boxShadow:"0 8px 28px rgba(0,0,0,.10)",zIndex:9999,overflow:"hidden"}}>
                {suggestions.map((s,i)=>(
                  <div key={i} className="srp-sugg" onMouseDown={()=>{setInputVal(s);handleSearch(s);}}
                    style={{padding:"10px 16px",fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",gap:10,color:C.sub,fontFamily:C.sans,background:C.white}}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={C.ghost} strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
                    {s}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mobile filters button */}
          {isMobile && (
            <button onClick={()=>setShowMobileFilters(true)} style={{
              position:"relative",
              display:"flex",alignItems:"center",justifyContent:"center",
              border:`1.5px solid ${C.green}`,
              background:C.greenTint,
              borderRadius:"50%", width:38,height:38,
              cursor:"pointer", flexShrink:0,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.greenDark} strokeWidth="2">
                <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
              </svg>
              {hasActiveFilters && <div style={{position:"absolute",top:2,right:2,width:7,height:7,borderRadius:"50%",background:C.green,border:`1.5px solid ${C.white}`}}/>}
            </button>
          )}
        </div>
      </div>

      {/* ── Business mode banner ── */}
      {isBusinessMode && (
        <div style={{background:C.greenTint,borderBottom:`1px solid ${C.greenBorder}`}}>
          <div style={{maxWidth:1440,margin:"0 auto",padding:isMobile?"9px 16px":"9px 16px",display:"flex",alignItems:"center",gap:8}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2.2">
              <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
            </svg>
            <span style={{fontSize:12.5,color:C.green,fontWeight:600,fontFamily:C.sans}}>Searching for business-eligible pros</span>
            <span style={{fontSize:12,color:C.sub,fontFamily:C.sans}}>— only professionals available for business bookings are shown.</span>
          </div>
        </div>
      )}

      {/* ── Body ── */}
      <div style={{
        maxWidth:1440,margin:"0 auto",
        padding:isMobile?"20px 16px 60px":"32px 16px 80px",
        display:"grid",
        gridTemplateColumns:isMobile?"1fr":isTablet?"200px 1fr":"240px 1fr",
        gap:isMobile?0:32,
        alignItems:"start",
      }}>

        {/* ── Sidebar ── */}
        {!isMobile && (
          <div className="srp-sidebar" style={{position:"sticky",top:NAVBAR_H+48+44}}>

            {/* Category */}
            <FilterSection title="Category">
              <div style={{display:"flex",flexDirection:"column",gap:2,maxHeight:340,overflowY:"auto"}}>
                <div onClick={()=>handleCategoryTab(null)} style={{
                  padding:"6px 0",cursor:"pointer",fontSize:13,fontFamily:C.sans,
                  color:!activeCategory?C.green:C.sub,fontWeight:!activeCategory?600:400,
                }}>All categories</div>
                {catsLoading ? (
                  [1,2,3,4].map(i=>(
                    <div key={i} className="srp-shimmer" style={{width:"70%",height:12,borderRadius:4,margin:"6px 0"}}/>
                  ))
                ) : (
                  (categories.length>0?categories:FALLBACK_CATEGORIES).map(cat=>{
                    const isActive = activeCategory?.id===cat.id;
                    const subs = CATEGORY_SUBSERVICES[cat.slug] || [];
                    return (
                      <div key={cat.id}>
                        <div onClick={()=>handleCategoryTab(cat)} style={{
                          padding:"6px 0",cursor:"pointer",fontSize:13,fontFamily:C.sans,
                          color:isActive?C.green:C.sub,fontWeight:isActive?600:400,
                          display:"flex",alignItems:"center",justifyContent:"space-between",
                        }}>
                          <span>{cat.name}</span>
                          {subs.length>0 && isActive && (
                            <svg width="10" height="10" viewBox="0 0 20 20" fill="none" stroke={C.green} strokeWidth="2.5" style={{transform:"rotate(180deg)"}}>
                              <path d="M5 8l5 5 5-5"/>
                            </svg>
                          )}
                        </div>
                        {isActive && subs.length>0 && (
                          <div style={{paddingLeft:14,marginTop:2,marginBottom:4}}>
                            {subs.map(sub=>(
                              <div key={sub} onClick={()=>handleSearch(sub)} style={{
                                padding:"5px 0",cursor:"pointer",fontSize:12,fontFamily:C.sans,
                                color:query.toLowerCase()===sub.toLowerCase()?C.green:C.faint,
                                fontWeight:query.toLowerCase()===sub.toLowerCase()?600:400,
                              }}>{sub}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </FilterSection>

            {/* Sort */}
            <div style={{marginBottom:20}}>
              <p style={{fontSize:11,fontWeight:700,color:C.ghost,textTransform:"uppercase",letterSpacing:".08em",margin:"0 0 10px",fontFamily:C.sans}}>Sort</p>
              <select value={sortBy} onChange={e=>{setSortBy(e.target.value);setCurrentPage(1);}}
                style={{width:"100%",padding:"9px 12px",borderRadius:10,border:`1.5px solid ${C.border}`,fontSize:13,fontFamily:C.sans,color:C.sub,background:C.white,outline:"none",cursor:"pointer",transition:C.tx}}
                onFocus={e=>e.target.style.borderColor=C.green}
                onBlur={e=>e.target.style.borderColor=C.border}>
                {SORT_OPTIONS.map(s=><option key={s}>{s}</option>)}
              </select>
            </div>

            {/* Location */}
            <FilterSection title="Location">
              <div ref={sidebarLocationDropRef} style={{position:"relative"}}>
                <button onClick={()=>setShowLocationDropSidebar(o=>!o)} style={{
                  width:"100%",padding:"9px 12px",borderRadius:10,
                  border:`1.5px solid ${showLocationDropSidebar?C.green:C.border}`,
                  fontSize:13,fontFamily:C.sans,color:C.sub,
                  background:C.white,cursor:"pointer",
                  display:"flex",alignItems:"center",justifyContent:"space-between",
                  boxShadow:showLocationDropSidebar?`0 0 0 3px ${C.greenTint}`:"none",
                  transition:C.tx,textAlign:"left",
                }}>
                  <span style={{fontFamily:C.sans,fontSize:13,color:C.sub}}>{location}</span>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke={C.ghost} strokeWidth="2.5"
                    style={{flexShrink:0,transition:"transform .18s",transform:showLocationDropSidebar?"rotate(180deg)":"rotate(0)"}}>
                    <path d="M5 8l5 5 5-5"/>
                  </svg>
                </button>
                {showLocationDropSidebar && (
                  <div id="srp-loc-drop" style={{
                    position:"absolute",top:"calc(100% + 4px)",left:0,right:0,
                    background:C.white,border:`1.5px solid ${C.border}`,borderRadius:10,
                    boxShadow:"0 8px 28px rgba(0,0,0,.10)",zIndex:400,
                    maxHeight:280,overflowY:"auto",
                  }}>
                    {locationOptions.map(l=>{
                      const subs = SUBREGIONS[l];
                      const isExpanded = subs && (location===l || subs.includes(location));
                      return (
                        <div key={l}>
                          <div onClick={()=>{ if(!subs) { handleLocationChange(l); setShowLocationDropSidebar(false); } else { handleLocationChange(l); } }}
                            style={{
                              padding:"9px 12px",fontSize:13,fontFamily:C.sans,
                              color:(l===location||subs?.includes(location))?C.green:C.sub,
                              fontWeight:(l===location||subs?.includes(location))?600:400,
                              background:(l===location)?C.greenTint:C.white,
                              cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",
                            }}>
                            <span>{l}</span>
                            {subs && (
                              <svg width="10" height="10" viewBox="0 0 20 20" fill="none" stroke={C.ghost} strokeWidth="2.5"
                                style={{transform:isExpanded?"rotate(180deg)":"rotate(0)",transition:"transform .15s"}}>
                                <path d="M5 8l5 5 5-5"/>
                              </svg>
                            )}
                          </div>
                          {subs && isExpanded && subs.map(sub=>(
                            <div key={sub} onClick={()=>{handleLocationChange(sub);setShowLocationDropSidebar(false);}}
                              style={{
                                padding:"7px 12px 7px 26px",fontSize:12.5,fontFamily:C.sans,
                                color:sub===location?C.green:C.faint,
                                fontWeight:sub===location?600:400,
                                background:sub===location?C.greenTint:C.white,
                                cursor:"pointer",
                              }}>{sub}</div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </FilterSection>

            <FilterSection title="Budget per job">
              <div style={{display:"flex",flexDirection:"column",gap:2}}>
                {BUDGET_OPTIONS.map(b=>(
                  <RadioOpt key={b.label} name="budget" label={b.label} checked={budget===b.label} onChange={()=>{setBudget(b.label);setCurrentPage(1);}}/>
                ))}
              </div>
            </FilterSection>

            <FilterSection title="Job success" defaultOpen={false}>
              <div style={{display:"flex",flexDirection:"column",gap:2}}>
                {SUCCESS_OPTIONS.map(s=>(
                  <RadioOpt key={s} name="success" label={s} checked={successMin===s} onChange={()=>{setSuccessMin(s);setCurrentPage(1);}}/>
                ))}
              </div>
            </FilterSection>

            <FilterSection title="Availability" defaultOpen={false}>
              <label style={{display:"flex",alignItems:"center",gap:9,cursor:"pointer"}}>
                <input type="checkbox" checked={availableOnly} onChange={e=>{setAvailableOnly(e.target.checked);setCurrentPage(1);}}
                  style={{width:14,height:14,accentColor:C.green,cursor:"pointer"}}/>
                <span style={{fontSize:13,color:C.sub,fontFamily:C.sans}}>Available now only</span>
              </label>
            </FilterSection>

            {hasActiveFilters && (
              <button onClick={clearAll} style={{
                background:"none",border:"none",color:C.green,
                fontSize:12.5,fontWeight:600,cursor:"pointer",padding:0,
                fontFamily:C.sans,textDecoration:"underline",marginTop:4,
              }}>Clear all filters</button>
            )}
          </div>
        )}

        {/* ── Results ── */}
        <div>
          {/* Page header */}
          <div style={{marginBottom:20}}>
            <h1 style={{
              fontFamily:C.serif,
              fontSize:isMobile?22:28,
              fontWeight:400,
              color:C.text,
              margin:"0 0 4px",
              letterSpacing:"-0.03em",
              fontStyle:"italic",
            }}>{headingText}</h1>
            {!prosLoading && (
              <p style={{fontFamily:C.sans,fontSize:13,color:C.faint,margin:0}}>
                {filtered.length.toLocaleString()} {filtered.length===1?"professional":"professionals"} found
                {activeCategory ? ` in ${activeCategory.name}` : ""}
                {location!=="All Locations" ? ` · ${location}` : ""}
              </p>
            )}
          </div>

          {/* Cards */}
          {prosLoading ? (
            <SearchLoadingIndicator isMobile={isMobile}/>
          ) : pagePros.length===0 ? (
            <EmptyState onClear={clearAll} isMobile={isMobile}/>
          ) : (
            <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(3, 1fr)",gap:isMobile?10:16}}>
              {pagePros.map(pro=>(
                <ProCard
                  key={pro.id} pro={pro}
                  onClick={()=>handleProClick(pro)}
                  ctaLabel={ctaLabel}
                  ctaHref={getCtaHref(pro)}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages>1 && (
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:5,marginTop:40,flexWrap:"wrap"}}>
              <button onClick={()=>{setCurrentPage(p=>Math.max(1,p-1));window.scrollTo(0,0);}} disabled={currentPage===1}
                style={{width:36,height:36,borderRadius:"50%",border:`1.5px solid ${C.border}`,background:C.white,cursor:currentPage===1?"default":"pointer",display:"flex",alignItems:"center",justifyContent:"center",opacity:currentPage===1?0.3:1,transition:C.tx}}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.sub} strokeWidth="2.2"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              {Array.from({length:Math.min(totalPages,isMobile?4:6)},(_,i)=>i+1).map(p=>(
                <button key={p} onClick={()=>{setCurrentPage(p);window.scrollTo(0,0);}} style={{
                  width:36,height:36,borderRadius:"50%",
                  border:`1.5px solid ${currentPage===p?C.green:C.border}`,
                  background:currentPage===p?C.green:C.white,
                  color:currentPage===p?"#fff":C.sub,
                  fontSize:13,fontWeight:currentPage===p?700:400,
                  cursor:"pointer",transition:C.tx,fontFamily:C.sans,
                }}>{p}</button>
              ))}
              {totalPages>(isMobile?4:6)&&<span style={{color:C.ghost,fontSize:14}}>…</span>}
              <button onClick={()=>{setCurrentPage(p=>Math.min(totalPages,p+1));window.scrollTo(0,0);}} disabled={currentPage===totalPages}
                style={{width:36,height:36,borderRadius:"50%",border:`1.5px solid ${C.border}`,background:C.white,cursor:currentPage===totalPages?"default":"pointer",display:"flex",alignItems:"center",justifyContent:"center",opacity:currentPage===totalPages?0.3:1,transition:C.tx}}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.sub} strokeWidth="2.2"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            </div>
          )}

          {/* Related searches */}
          {!prosLoading && query && (
            <div style={{marginTop:48,paddingTop:28,borderTop:`1px solid ${C.borderFaint}`}}>
              <h3 style={{fontFamily:C.sans,fontSize:12,fontWeight:700,color:C.ghost,textTransform:"uppercase",letterSpacing:".09em",margin:"0 0 14px"}}>
                People also searched for
              </h3>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {(() => {
                  const catSubs = activeCategory ? (CATEGORY_SUBSERVICES[activeCategory.slug] || []) : [];
                  const rest = ALL_SUGGESTIONS.filter(s => !catSubs.includes(s));
                  const ranked = [...catSubs, ...rest].filter(s => s.toLowerCase() !== query.toLowerCase());
                  const locLabel = location !== "All Locations" ? ` in ${location}` : "";
                  return ranked.slice(0, isMobile ? 10 : 16).map((s, i) => (
                    <RelatedPill key={i} label={s + locLabel} onClick={() => handleSearch(s)} isMobile={isMobile} />
                  ));
                })()}
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer/>
    </div>
  );
}